import {
  AbstractMesh,
  ActionEvent,
  AnimationGroup,
  Axis,
  Color3,
  Color4,
  ContainerAssetTask,
  CubeTexture,
  GroundMesh,
  HDRCubeTexture,
  IPhysicsCollisionEvent,
  Material,
  Matrix,
  Mesh,
  MeshAssetTask,
  MeshBuilder,
  PBRMaterial,
  PhysicsBody,
  PhysicsMassProperties,
  PhysicsMaterial,
  PhysicsMotionType,
  PhysicsShape,
  PhysicsShapeBox,
  PhysicsShapeCapsule,
  PhysicsShapeConvexHull,
  PhysicsShapeCylinder,
  PhysicsShapeMesh,
  PhysicsShapeSphere,
  PhysicsShapeType,
  Scene,
  Skeleton,
  StandardMaterial,
  Texture,
  TransformNode,
  Vector2,
  Vector3,
  VertexBuffer,
  VertexData,
} from '@babylonjs/core';
import { ImportMeshAsync } from '@babylonjs/core/Loading/sceneLoader';
import { TerrainMaterial } from '@babylonjs/materials';
import { Builder, IBuilder } from 'builder-pattern';
import { Asset } from './Asset';
import { AssetObject } from './AssetObject';
import { MeshBehavior } from './behavior/MeshBehavior';
import { ImageUtils } from './ImageUtils';
import { Level } from './Level';
import { Noise } from './Noise';
import { Player } from './Player';
import { PlayerCollision } from './PlayerCollision';

export abstract class MeshConfig {
  name: string;
  position: Vector3;
  size: Vector3;
  material: Material;
  materialName: string;
  castShadows: boolean;
  receiveShadows: boolean;
  physicsShapeType: PhysicsShapeType;
  physicsMaterial: PhysicsMaterial;
  physicsMotionType: PhysicsMotionType;
  physicsMassProperties: PhysicsMassProperties;
  physicsShapeTrigger: boolean;
  disablePreStep?: boolean;
  rotation?: Vector3;
  bakeRotation?: Vector3;
  scale?: number;
  highlight?: Color3;
  metadata?: any;
  behaviors?: MeshBehavior[];
  onPlayerCollision?: (player: Player, mesh: Mesh) => void;

  static getDefaults(): any {
    return {
      castShadows: true,
      receiveShadows: true,
      physicsShapeTrigger: false,
      physicsShapeType: PhysicsShapeType.BOX,
      physicsMaterial: { friction: 1, restitution: 0 },
      physicsMassProperties: { mass: 0 },
      physicsMotionType: PhysicsMotionType.STATIC,
    };
  }

  abstract buildMesh(level: Level): Mesh;
}

export class AssetObjectConfig extends MeshConfig {
  static builder(): IBuilder<AssetObjectConfig> {
    return Builder(AssetObjectConfig, MeshConfig.getDefaults());
  }

  object: AssetObject;

  skeleton: Skeleton;
  animations: AnimationGroup[];
  bakeRotation: Vector3;

  buildMesh(level: Level): Mesh {
    const asset = level.game.assetFactory.getAsset(this.object.asset);
    const task: MeshAssetTask | ContainerAssetTask = asset.getTask();
    let mesh: Mesh;

    if (asset.singleMesh) {
      mesh = asset.getSingleMesh() as Mesh;
    } else if (asset.meshIndex !== undefined) {
      mesh = task.loadedMeshes[asset.meshIndex] as Mesh;
    } else {
      let meshes = [];

      if (this.object.name) {
        meshes = asset.getMeshes(this.object.name);
      } else {
        meshes = task.loadedMeshes;
      }

      meshes = Asset.filterMeshes(meshes);
      if (meshes?.length === 1) {
        mesh = meshes[0] as Mesh;
      } else if (meshes?.length > 1) {
        // MergeMeshes with disposeSource=false to preserve materials, but hide source meshes
        mesh = Mesh.MergeMeshes(meshes as Mesh[], false, true, undefined, true, true);
        // Hide source meshes so they don't appear at 0,0,0
        this.setInvisible(...meshes);
        if (typeof this.name === 'string') {
          mesh.name = (this.name as string).replace('*', '');
        }
      }
    }

    if (!mesh) {
      // try with transform node
      let meshes = [];
      const tn = asset.getTransformNode(this.object.name as string);
      if (tn) {
        meshes = Asset.filterMeshes(tn.getChildMeshes());
      }
      if (meshes?.length === 1) {
        mesh = meshes[0] as Mesh;
      } else if (meshes?.length > 1) {
        // MergeMeshes with disposeSource=false to preserve materials, but hide source meshes
        mesh = Mesh.MergeMeshes(meshes as Mesh[], false, true, undefined, true, true);
        // Hide source meshes so they don't appear at 0,0,0
        this.setInvisible(...meshes);
        if (typeof this.name === 'string') {
          mesh.name = (this.name as string).replace('*', '');
        }
      }
    }

    if (!mesh) {
      console.warn('Could not find mesh with name', this.object.name, 'in asset', this.object.asset);
      return;
    }

    if (this.object.clone) {
      const sourceMesh = mesh;
      mesh = mesh.clone(this.name);
      // Hide the source mesh so it doesn't appear at 0,0,0
      this.setInvisible(sourceMesh);
      // Make the cloned mesh visible (source was hidden by default)
      mesh.isVisible = true;
      mesh.setEnabled(true);
      // Detach from any parent to avoid inherited transforms
      mesh.setParent(null);
      this.resetTransforms(mesh);
    }

    if (this.object.deepClone) {
      if (!(mesh instanceof Mesh)) {
        // Find first mesh child
        const meshChildren = (mesh as TransformNode).getChildMeshes(false, (node) => node instanceof Mesh);
        mesh = meshChildren[0] as Mesh;
      }

      const sourceMesh = mesh;
      const skeleton: Skeleton = asset.getSkeleton();
      const animations: AnimationGroup[] = asset.getAnimationGroups();

      // Clone mesh with skeleton and animations using MeshFactory.cloneMesh
      const {
        mesh: clonedMesh,
        animations: clonedAnimations,
        skeleton: clonedSkeleton,
      } = MeshFactory.cloneMesh(mesh, skeleton, animations, this.name, 'clone');

      // Hide the source mesh so it doesn't appear at 0,0,0
      this.setInvisible(sourceMesh);

      this.animations = clonedAnimations;
      this.skeleton = clonedSkeleton;
      mesh = clonedMesh as Mesh;
      this.resetTransforms(mesh);
    }

    // Bake rotation into the (cloned) mesh vertices so it becomes the permanent base orientation.
    // Must run AFTER cloning so each instance gets its own bake, not the shared original.
    if (this.bakeRotation) {
      mesh.rotation = this.bakeRotation.clone();
      mesh.bakeTransformIntoVertices(mesh.computeWorldMatrix(true));
      mesh.rotation.setAll(0);
    }

    // Center the mesh origin on its bounding box center
    if (this.object.centerOrigin) {
      if (this.object.computeCenter) {
        mesh.bakeTransformIntoVertices(mesh.computeWorldMatrix(true));
        const boundingInfo = mesh.getBoundingInfo();
        const center = boundingInfo.boundingBox.center;
        // Move vertices so the center becomes the origin
        mesh.position = center.scale(-1);
        mesh.bakeCurrentTransformIntoVertices();
        mesh.refreshBoundingInfo();
      } else {
        const boundingInfo = mesh.getBoundingInfo();
        const center = boundingInfo.boundingBox.center;
        // Move vertices so the center becomes the origin
        mesh.bakeTransformIntoVertices(mesh.computeWorldMatrix(true));
        mesh.position.set(0, 0, 0);
        mesh.setPivotPoint(center.scale(-1));
        mesh.bakeCurrentTransformIntoVertices();
        mesh.setPivotPoint(Vector3.Zero());
        mesh.refreshBoundingInfo();
      }
    }

    if (this.size) {
      // Calculate uniform scale to fit mesh within target size without deformation
      // const boundingInfo = mesh.getBoundingInfo();
      // const meshSize = boundingInfo.boundingBox.extendSize.scale(2); // Full size of bounding box

      // // Calculate scale factors for each axis
      // const scaleX = this.size.x / meshSize.x;
      // const scaleY = this.size.y / meshSize.y;
      // const scaleZ = this.size.z / meshSize.z;

      // // Use the minimum scale factor to ensure mesh fits within target size
      // const uniformScale = Math.min(scaleX, scaleY, scaleZ);
      // mesh.scaling.setAll(uniformScale);

      mesh.scaling.set(this.size.x, this.size.y, this.size.z);
    }

    if (this.object.metallic || this.object.roughness || this.object.diffuseColor) {
      this.applyMaterialProperties(mesh);
    }

    // Ensure the mesh is visible (source meshes are hidden by default after loading)
    mesh.isVisible = true;
    mesh.setEnabled(true);

    return mesh;
  }

  setInvisible(...meshes: Mesh[]) {
    for (const mesh of meshes) {
      mesh.isVisible = false;
      mesh.setEnabled(false);
      mesh.getChildMeshes().forEach((m) => {
        m.isVisible = false;
        m.setEnabled(false);
      });
    }
  }

  applyMaterialProperties(mesh: Mesh) {
    const meshes = mesh.getChildMeshes().length > 0 ? mesh.getChildMeshes() : [mesh];

    meshes.forEach((m) => {
      if (m.material instanceof StandardMaterial) {
        m.material.diffuseColor =
          this.object.diffuseColor || new Color3(this.object.roughness, this.object.roughness, this.object.roughness);
        m.material.specularColor = new Color3(this.object.metallic, this.object.metallic, this.object.metallic);
      }
      if (m.material instanceof PBRMaterial) {
        if (this.object.diffuseColor) {
          m.material.albedoColor = this.object.diffuseColor;
        }
        if (this.object.metallic) {
          m.material.metallic = this.object.metallic;
        }
        if (this.object.roughness) {
          m.material.roughness = this.object.roughness;
        }
      }
    });
  }

  resetTransforms(mesh: AbstractMesh) {
    // Reset all transforms so platform positioning works correctly
    mesh.position.set(0, 0, 0);
    mesh.rotation.set(0, 0, 0);
    mesh.scaling.set(1, 1, 1);
    mesh.rotationQuaternion = null; // Clear quaternion to use euler angles
  }
}

export class ExistingMeshConfig extends MeshConfig {
  static builder(): IBuilder<ExistingMeshConfig> {
    return Builder(ExistingMeshConfig, MeshConfig.getDefaults());
  }

  mesh: Mesh;

  buildMesh(level: Level): Mesh {
    return this.mesh;
  }
}

export class BoxConfig extends MeshConfig {
  static builder(): IBuilder<BoxConfig> {
    return Builder(BoxConfig, MeshConfig.getDefaults());
  }

  buildMesh(level: Level): Mesh {
    return MeshBuilder.CreateBox(this.name, { width: this.size.x, height: this.size.y, depth: this.size.z }, level.scene);
  }
}

export class CylinderConfig extends MeshConfig {
  static builder(): IBuilder<CylinderConfig> {
    return Builder(CylinderConfig, MeshConfig.getDefaults());
  }
  tessellation?: number;
  subdivisions?: number;
  arc?: number;
  hasRings?: boolean;
  enclose?: boolean;
  cap?: number;

  buildMesh(level: Level): Mesh {
    return MeshBuilder.CreateCylinder(
      this.name,
      {
        height: this.size.y,
        diameterTop: this.size.x,
        diameterBottom: this.size.z,
        tessellation: this.tessellation,
        subdivisions: this.subdivisions,
        arc: this.arc,
        hasRings: this.hasRings,
        enclose: this.enclose,
        cap: this.cap,
      },
      level.scene
    );
  }
}

export class SphereConfig extends MeshConfig {
  static builder(): IBuilder<SphereConfig> {
    return Builder(SphereConfig, MeshConfig.getDefaults());
  }
  segments?: number;
  arc?: number;
  slice?: number;

  buildMesh(level: Level): Mesh {
    return MeshBuilder.CreateSphere(
      this.name,
      { diameterX: this.size.x, diameterY: this.size.y, diameterZ: this.size.z, segments: this.segments, arc: this.arc, slice: this.slice },
      level.scene
    );
  }
}

export class TorusConfig extends MeshConfig {
  static builder(): IBuilder<TorusConfig> {
    return Builder(TorusConfig, MeshConfig.getDefaults());
  }
  diameter?: number;
  thickness?: number;
  tessellation?: number;

  buildMesh(level: Level): Mesh {
    return MeshBuilder.CreateTorus(
      this.name,
      { diameter: this.size.x, thickness: this.size.y, tessellation: this.tessellation },
      level.scene
    );
  }
}

export class DiscConfig extends MeshConfig {
  static builder(): IBuilder<DiscConfig> {
    return Builder(DiscConfig, MeshConfig.getDefaults());
  }
  radius?: number;
  tessellation?: number;
  arc?: number;

  buildMesh(level: Level): Mesh {
    return MeshBuilder.CreateDisc(this.name, { radius: this.size.x, tessellation: this.tessellation, arc: this.arc }, level.scene);
  }
}

/**
 * Skybox configuration
 */
export interface SkyBoxConfig {
  /**
   * The color to use when no texture is available
   */
  clearColor?: Color4;
  /**
   * The URL to use for the skybox
   */
  url?: string;
  /**
   * The extension to use for the skybox (jpg or png => in case of multiple images)
   */
  imageExtension?: string;
  /**
   * The scale to use for the skybox
   */
  scale?: number;
  /**
   * The position to use for the skybox
   */
  position?: Vector3;
  /**
   * The rotation to use for the skybox
   */
  rotation?: Vector3;
  /**
   * The rotation axis to use for the skybox
   */
  rotationAxis?: string;
  /**
   * The rotation speed to use for the skybox
   */
  rotationSpeed?: number;
  /**
   * Whether to use a photo dome for the skybox
   */
  photoDome?: boolean;
  /**
   * The name to use for the skybox mesh
   */
  meshName?: string;
}

export class GroundConfig extends MeshConfig {
  static builder(): IBuilder<GroundConfig> {
    return Builder(GroundConfig, MeshConfig.getDefaults());
  }
  width?: number;
  height?: number;
  subdivisions?: number;
  subdivisionsX?: number;
  subdivisionsY?: number;

  buildMesh(level: Level): Mesh {
    return MeshBuilder.CreateGround(
      this.name,
      {
        width: this.size.x,
        height: this.size.y,
        subdivisions: this.subdivisions,
        subdivisionsX: this.subdivisionsX,
        subdivisionsY: this.subdivisionsY,
      },
      level.scene
    );
  }
}

export class HeightMapConfig extends MeshConfig {
  static builder(): IBuilder<HeightMapConfig> {
    return Builder(HeightMapConfig, {
      receiveShadows: true,
      castShadows: true,
      physicsShapeTrigger: false,
      physicsMotionType: PhysicsMotionType.STATIC,
      physicsShapeType: PhysicsShapeType.MESH,
      physicsMaterial: { friction: 1, restitution: 0 },
      physicsMassProperties: { mass: 1 },
      name: 'gdhm',
      position: Vector3.Zero(),
      width: 100,
      height: 100,
      subdivisions: 100,
      maxHeight: 10,
      minHeight: 0,
      terrainTextureScale: Vector3.One(),
      backFaceCulling: false,
      specularColor: new Color3(0.5, 0.5, 0.5),
      diffuseColor: new Color3(0.5, 0.5, 0.5),
      usePhysics: true,
      updatable: false,
    } as any);
  }

  width: number;
  height: number;
  subdivisions: number;
  maxHeight: number;
  minHeight: number;
  updatable: boolean;
  heightMapImageUrl: string;
  specularColor: Color3;
  diffuseColor: Color3;
  terrainTextureScale: Vector3;
  terrainTexture1?: Texture;
  terrainTexture2?: Texture;
  terrainTexture3?: Texture;

  terrainTextureAtlas?: string;
  terrainTextureName1: string;
  terrainTextureName2: string;
  terrainTextureName3: string;

  mixTexture: Texture;
  backFaceCulling: boolean;
  physicsShapeType: PhysicsShapeType;
  mixColorMap?: Map<number, number[]>;
  lowerPoint?: (point: Vector2) => number;

  // Path integration fields
  pathPoints?: Vector3[]; // The path points to align heightmap to
  pathWidth?: number; // Width of the flat corridor (default: 10)
  pathTransitionWidth?: number; // Width of the transition/walls (default: 5)
  pathYOffset?: number; // Y offset relative to path height (default: 0)
  pathProjection?: boolean; // Project path to heightmap

  buildMesh(level: Level): Mesh {
    throw new Error('Method not implemented.');
  }

  async buildMeshAsync(level: Level): Promise<GroundMesh> {
    const heightMapImage = await ImageUtils.loadImage(this.heightMapImageUrl);
    return new Promise<GroundMesh>((resolve, reject) => {
      MeshBuilder.CreateGroundFromHeightMap(this.name, heightMapImage.src, {
        width: this.width,
        height: this.height,
        subdivisions: this.subdivisions,
        maxHeight: this.maxHeight,
        minHeight: this.minHeight,
        updatable: this.updatable,
        onReady: async (mesh: GroundMesh) => {
          if (!this.material) {
            // Create terrain material

            if (!this.mixTexture) {
              let mixMapImage = await ImageUtils.generateMixMapFromHeightMap(heightMapImage, this.mixColorMap);

              // Paint path onto mix map using green channel (diffuseTexture2)
              if (this.pathProjection && this.pathPoints && this.pathPoints.length > 1) {
                const projectionWidth = this.pathWidth > 0 ? this.pathWidth : 10;
                mixMapImage = await ImageUtils.paintPathOnMixMap(mixMapImage, this.pathPoints, this.width, this.height, projectionWidth);
              }

              const mixTexture = new Texture(mixMapImage.src, level.game.scene);
              mixTexture.name = this.name + 'Mixmap';
              this.mixTexture = mixTexture;
            }

            const terrainMaterial = new TerrainMaterial(this.name + 'Material', level.game.scene);
            terrainMaterial.backFaceCulling = this.backFaceCulling;
            terrainMaterial.specularColor = this.specularColor;
            terrainMaterial.diffuseColor = this.diffuseColor;
            terrainMaterial.mixTexture = this.mixTexture;

            if (this.terrainTextureAtlas) {
              this.terrainTexture1 = level.getAtlasByName(this.terrainTextureAtlas).createTexture(this.terrainTextureName1, true);
              this.terrainTexture2 = level.getAtlasByName(this.terrainTextureAtlas).createTexture(this.terrainTextureName2, true);
              this.terrainTexture3 = level.getAtlasByName(this.terrainTextureAtlas).createTexture(this.terrainTextureName3, true);
            } else {
              this.terrainTexture1 = level.getTexture(this.terrainTextureName1);
              this.terrainTexture2 = level.getTexture(this.terrainTextureName2);
              this.terrainTexture3 = level.getTexture(this.terrainTextureName3);
            }

            // Diffuse textures following the RGB values of the mix map
            // diffuseTexture1: Red
            // diffuseTexture2: Green
            // diffuseTexture3: Blue
            terrainMaterial.diffuseTexture1 = this.terrainTexture1;
            terrainMaterial.diffuseTexture2 = this.terrainTexture2;
            terrainMaterial.diffuseTexture3 = this.terrainTexture3;

            // Rescale textures according to the terrain
            terrainMaterial.diffuseTexture1.uScale = terrainMaterial.diffuseTexture1.vScale = this.terrainTextureScale.x;
            terrainMaterial.diffuseTexture2.uScale = terrainMaterial.diffuseTexture2.vScale = this.terrainTextureScale.y;
            terrainMaterial.diffuseTexture3.uScale = terrainMaterial.diffuseTexture3.vScale = this.terrainTextureScale.z;

            this.material = terrainMaterial;
          }

          // Apply path integration (Schneise/Sockel)
          if (this.pathPoints && this.pathPoints.length > 0 && this.pathWidth > 0 && this.pathYOffset) {
            this.applyPathToVertices(mesh);
          }

          if (this.lowerPoint) {
            const positions = mesh.getVerticesData(VertexBuffer.PositionKind);
            const position = Vector3.Zero();

            for (let index = 0; index < positions.length; index += 3) {
              Vector3.FromArrayToRef(positions, index, position);
              const point = new Vector2(mesh.position.x + position.x, mesh.position.z + position.z);

              const y = this.lowerPoint(point);
              if (y !== undefined) {
                position.y = y;
                position.toArray(positions, index);
              }
            }
            mesh.updateVerticesData(VertexBuffer.PositionKind, positions);
          }

          MeshFactory.configureMesh(mesh, this, level);

          mesh.checkCollisions = true;

          resolve(mesh);
        },
      });
    });
  }

  /**
   * Find the closest point on the path and return distance and height.
   * Uses XZ distance only (horizontal plane).
   */
  private findClosestPathPoint(x: number, z: number): { distance: number; pathY: number } {
    let minDistSq = Infinity;
    let closestY = 0;

    for (const pathPoint of this.pathPoints) {
      const dx = x - pathPoint.x;
      const dz = z - pathPoint.z;
      const distSq = dx * dx + dz * dz;

      if (distSq < minDistSq) {
        minDistSq = distSq;
        closestY = pathPoint.y;
      }
    }

    return { distance: Math.sqrt(minDistSq), pathY: closestY };
  }

  /**
   * Smooth easing function for transition.
   */
  private easeInOutCubic(t: number): number {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  /**
   * Apply path to heightmap vertices:
   * - Creates a corridor where path is below terrain
   * - Creates a pedestal where path is above terrain
   */
  private applyPathToVertices(mesh: GroundMesh): void {
    const pathWidth = this.pathWidth ?? 10;
    const transitionWidth = this.pathTransitionWidth ?? 5;
    const yOffset = this.pathYOffset ?? 0;
    const halfPathWidth = pathWidth / 2;

    const positions = mesh.getVerticesData(VertexBuffer.PositionKind);
    if (!positions) return;

    for (let index = 0; index < positions.length; index += 3) {
      const vertexX = mesh.position.x + positions[index];
      const vertexZ = mesh.position.z + positions[index + 2];
      const vertexY = positions[index + 1];

      // Find closest path point
      const { distance, pathY } = this.findClosestPathPoint(vertexX, vertexZ);
      const targetY = pathY + yOffset;

      if (distance <= halfPathWidth) {
        // Inside the corridor: set to path height
        positions[index + 1] = targetY;
      } else if (distance <= halfPathWidth + transitionWidth) {
        // In transition zone: interpolate between path height and original
        const t = (distance - halfPathWidth) / transitionWidth;
        const easedT = this.easeInOutCubic(t);
        positions[index + 1] = targetY + (vertexY - targetY) * easedT;
      }
      // Outside transition: keep original height
    }

    mesh.updateVerticesData(VertexBuffer.PositionKind, positions);
    mesh.refreshBoundingInfo();
  }
}

export class MeshFactory {
  constructor(public level: Level) {}

  /**
   * Clones a mesh, its skeleton and animations.
   *
   * @param mesh the mesh to clone
   * @param skeleton skeleton to clone
   * @param animations animations to clone
   * @param name name of the clone
   * @param suffix suffix to add to the name
   * @returns cloned mesh, animations and skeleton
   */
  static cloneMesh(
    mesh: AbstractMesh,
    skeleton: Skeleton,
    animations: AnimationGroup[],
    name: string,
    suffix: string
  ): { mesh: TransformNode; animations: AnimationGroup[]; skeleton: Skeleton } {
    const clonedMesh = mesh.instantiateHierarchy(null, { doNotInstantiate: true }, (source, clone) => {
      clone.name = source.name;
      // Make cloned mesh visible (source was hidden by default)
      if (clone instanceof Mesh) {
        clone.isVisible = true;
      }
      clone.setEnabled(true);
    });

    clonedMesh.name = name + '.' + suffix;
    clonedMesh.setEnabled(true);
    if (clonedMesh instanceof Mesh) {
      clonedMesh.isVisible = true;
    }
    const map = {};
    let clonedSkeleton: Skeleton;
    if (skeleton) {
      clonedSkeleton = skeleton.clone(name + '.skel.' + suffix);

      const descendants = clonedMesh.getDescendants(false);
      for (let i = 0; i < descendants.length; i++) {
        if ((descendants[i] as any).subMeshes && (descendants[i] as any).skeleton) {
          (descendants[i] as any).skeleton = clonedSkeleton;
        }
        map[descendants[i].name] = descendants[i];
      }

      for (const bone of clonedSkeleton.bones) {
        const tf = bone.getTransformNode();
        if (tf && map[tf.name]) {
          bone.linkTransformNode(map[tf.name]);
        }
      }
    }
    const clonedAnimations = [];
    if (animations?.length > 0) {
      animations.forEach((ag) => {
        const clone = ag.clone(ag.name, (oldTarget) => {
          const newTarget = map[oldTarget.name];
          return newTarget || oldTarget;
        });
        clone.name = name + '.' + clone.name;
        clonedAnimations.push(clone);
      });
    }
    return { mesh: clonedMesh, animations: clonedAnimations, skeleton: clonedSkeleton };
  }

  static configureMesh(mesh: Mesh, config: MeshConfig, level: Level) {
    if (config.material) {
      mesh.material = config.material;
    }
    if (config.materialName) {
      mesh.material = level.getMaterial(config.materialName);
    }

    if (config.position) {
      mesh.position.addInPlace(config.position);
    }

    if (config.rotation) {
      if (config.rotation.x) {
        mesh.rotate(Axis.X, config.rotation.x);
      }
      if (config.rotation.y) {
        mesh.rotate(Axis.Y, config.rotation.y);
      }
      if (config.rotation.z) {
        mesh.rotate(Axis.Z, config.rotation.z);
      }
    }

    if (config.scale) {
      mesh.scaling.scaleInPlace(config.scale);
    }

    // physics body is only created, if physicsShapeType is set
    if (config.physicsShapeType !== undefined) {
      let shape: PhysicsShape = undefined;
      if (config.physicsShapeType === PhysicsShapeType.BOX) {
        shape = PhysicsShapeBox.FromMesh(mesh);
      } else if (config.physicsShapeType === PhysicsShapeType.SPHERE) {
        shape = PhysicsShapeSphere.FromMesh(mesh);
      } else if (config.physicsShapeType === PhysicsShapeType.CAPSULE) {
        shape = PhysicsShapeCapsule.FromMesh(mesh);
      } else if (config.physicsShapeType === PhysicsShapeType.CYLINDER) {
        shape = PhysicsShapeCylinder.FromMesh(mesh);
      } else if (config.physicsShapeType === PhysicsShapeType.CONVEX_HULL) {
        shape = new PhysicsShapeConvexHull(mesh, level.scene);
      } else if (config.physicsShapeType === PhysicsShapeType.MESH) {
        shape = new PhysicsShapeMesh(mesh, level.scene);
      }

      shape.isTrigger = config.physicsShapeTrigger;

      const body = new PhysicsBody(mesh, config.physicsMotionType ? config.physicsMotionType : PhysicsMotionType.STATIC, true, level.scene);
      shape.material = config.physicsMaterial;
      body.shape = shape;
      body.setMassProperties(config.physicsMassProperties);

      if (level.game.config.debug && level.game.config.debugBodies) {
        level.game.physicsViewer.showBody(body);
      }

      if (config.disablePreStep !== undefined) {
        body.disablePreStep = config.disablePreStep;
      }

      if (config.onPlayerCollision) {
        level.game.physicsPlugin.onTriggerCollisionObservable.add((collisionEvent) => {
          if (collisionEvent.type === 'TRIGGER_ENTERED') {
            const colliderBody = collisionEvent.collider;
            const triggerBody = collisionEvent.collidedAgainst;

            const isOurTrigger = triggerBody.shape === shape || colliderBody.shape === shape;
            if (!isOurTrigger) return;

            const playerShape = level.player?.characterController?.shape;
            if (colliderBody.shape === playerShape || triggerBody.shape === playerShape) {
              config.onPlayerCollision(level.player, mesh);
            }
          }
        });

        // body.setCollisionCallbackEnabled(true);
        // body.setCollisionEndedCallbackEnabled(true);
        //
        // body.getCollisionObservable().add((event: IPhysicsCollisionEvent, eventState: EventState) => {
        //   if (event?.collidedAgainst?.transformNode?.isDisposed()) {
        //     return;
        //   }
        //   const playerShape = level.game.player?.characterController?.shape;
        //   if (event?.collidedAgainst?.shape === playerShape) {
        //     config.onPlayerCollision(level.game.player, mesh);
        //   }
        // });
      }
    } else if (config.onPlayerCollision) {
      mesh.checkCollisions = true;
      mesh.metadata = mesh.metadata || {};
      mesh.metadata.playerCollision = new PlayerCollision(
        true,
        (level: Level, player: Player, event: ActionEvent | IPhysicsCollisionEvent) => {
          const m = (event as ActionEvent).additionalData as Mesh;
          config.onPlayerCollision(player, m);
        }
      );
      level.collisionMeshes.push(mesh);
    }

    if (config.receiveShadows) {
      mesh.receiveShadows = true;
    }

    if (config.castShadows) {
      level.addShadowCaster(mesh);
    }

    if (config.highlight) {
      level.highlights.addMesh(mesh, config.highlight, false);
    }

    if (config.metadata !== undefined) {
      mesh.metadata = config.metadata;
    }

    if (config.behaviors) {
      // Clone behaviors so each mesh instance has its own behavior instances
      const clonedBehaviors = config.behaviors.map((b) => b.clone());
      for (const behavior of clonedBehaviors) {
        behavior.initLevel(level);
        mesh.addBehavior(behavior);
      }

      level.game.scene.registerBeforeRender(() => {
        if (mesh && !mesh.isDisposed()) {
          for (const behavior of clonedBehaviors) {
            behavior.beforeRender();
          }
        }
      });
    }

    mesh.isPickable = false;
  }

  /**
   * Overlays a part of a ground mesh
   *
   * @param groundMesh the mesh to overlay
   * @param position center of the overlay (x, z), y is an offset
   * @param size with and depth of the overlay
   * @returns overlay
   */
  static createHeightMapOverlay(
    name: string,
    groundMesh: GroundMesh,
    position: Vector3,
    size: Vector2,
    subdivisions: number,
    material: Material,
    level: Level
  ): GroundMesh {
    const overlay = MeshBuilder.CreateGround(
      name,
      {
        width: size.x,
        height: size.y,
        subdivisions,
      },
      level.scene
    );

    //modify y position of the overlay according to the HeightMesh
    const positions = overlay.getVerticesData('position');
    for (let i = 0; i < positions.length; i += 3) {
      const x = positions[i] + position.x;
      const z = positions[i + 2] + position.z;
      positions[i + 1] = groundMesh.getHeightAtCoordinates(x, z) + position.y;
    }
    overlay.setVerticesData('position', positions);

    overlay.material = material;

    overlay.position.x = position.x;
    overlay.position.z = position.z;

    overlay.receiveShadows = true;
    overlay.isPickable = false;

    return overlay;
  }

  /**
   * Creates a block overlay that follows the relief of a ground mesh and has closed side walls.
   *
   * @param name Name of the mesh
   * @param groundMesh The height mesh to follow
   * @param position Center of the overlay (x, z), y is an offset
   * @param size Size of the overlay (width, depth)
   * @param subdivisions Number of subdivisions per side (higher = more detail)
   * @param height Height of the side walls (how deep the block goes down)
   * @param material Material for the overlay
   * @returns The created mesh
   */
  static createHeightMapBlockOverlay(
    name: string,
    groundMesh: GroundMesh,
    position: Vector3,
    size: Vector2,
    subdivisions: number,
    height: number,
    material: Material,
    level: Level
  ): Mesh {
    const positions: number[] = [];
    const indices: number[] = [];
    const uvs: number[] = [];

    // 1. Create the top surface (follows the relief)
    const grid: Vector3[][] = [];
    for (let i = 0; i <= subdivisions; i++) {
      grid[i] = [];
      for (let j = 0; j <= subdivisions; j++) {
        const x = position.x - size.x / 2 + (i * size.x) / subdivisions;
        const z = position.z - size.y / 2 + (j * size.y) / subdivisions;
        const y = groundMesh.getHeightAtCoordinates(x, z) + position.y;
        grid[i][j] = new Vector3(x, y, z);
        positions.push(x, y, z);
        uvs.push(i / subdivisions, j / subdivisions);
      }
    }

    // 2. Create the bottom surface (flat, at y = minY)
    const minY = position.y - height;
    const baseOffset = positions.length / 3;
    for (let i = 0; i <= subdivisions; i++) {
      for (let j = 0; j <= subdivisions; j++) {
        const x = position.x - size.x / 2 + (i * size.x) / subdivisions;
        const z = position.z - size.y / 2 + (j * size.y) / subdivisions;
        positions.push(x, minY, z);
        uvs.push(i / subdivisions, j / subdivisions);
      }
    }

    // 3. Indices for top and bottom surface
    for (let i = 0; i < subdivisions; i++) {
      for (let j = 0; j < subdivisions; j++) {
        const a = i * (subdivisions + 1) + j;
        const b = (i + 1) * (subdivisions + 1) + j;
        const c = (i + 1) * (subdivisions + 1) + (j + 1);
        const d = i * (subdivisions + 1) + (j + 1);

        // Top surface
        indices.push(a, b, d, b, c, d);

        // Bottom surface (flipped)
        const a2 = baseOffset + a;
        const b2 = baseOffset + b;
        const c2 = baseOffset + c;
        const d2 = baseOffset + d;
        indices.push(a2, d2, b2, b2, d2, c2);
      }
    }

    // 4. Side walls
    function addSide(i1: number, j1: number, i2: number, j2: number) {
      const top1 = i1 * (subdivisions + 1) + j1;
      const top2 = i2 * (subdivisions + 1) + j2;
      const bot1 = baseOffset + top1;
      const bot2 = baseOffset + top2;
      indices.push(top1, bot1, top2, top2, bot1, bot2);
    }
    // Four sides
    for (let i = 0; i < subdivisions; i++) {
      // front (j=0)
      addSide(i, 0, i + 1, 0);
      // back (j=max)
      addSide(i, subdivisions, i + 1, subdivisions);
      // left (i=0)
      addSide(0, i, 0, i + 1);
      // right (i=max)
      addSide(subdivisions, i, subdivisions, i + 1);
    }

    // 5. Build the mesh
    const mesh = new Mesh(name, level.scene);
    const vertexData = new VertexData();
    vertexData.positions = positions;
    vertexData.indices = indices;
    vertexData.uvs = uvs;
    vertexData.applyToMesh(mesh);
    mesh.material = material;
    mesh.receiveShadows = true;
    mesh.isPickable = false;

    return mesh;
  }

  static async createSkyBox(config: SkyBoxConfig, name: string, scene: Scene): Promise<Mesh> {
    let url = config.url;
    const isSingle = url.includes('.jpeg') || url.includes('.jpg') || url.includes('.png');
    const isHdr = url.includes('.hdr');
    const isMesh = url.includes('.glb') || url.includes('.gltf');
    const rotationSpeed = config.rotationSpeed || 0.000001; // Radians per millisecond

    if (isMesh) {
      //load the skybox mesh
      const result = await ImportMeshAsync(url, scene);

      // Stop any existing animations
      if (result.animationGroups) {
        result.animationGroups.forEach((ag) => ag.stop());
      }

      // Find the target mesh - either by name or take the first mesh with vertices
      let skybox: Mesh;
      if (config.meshName) {
        skybox = result.meshes.find((m) => m.getTotalVertices() > 0 && m.name === config.meshName) as Mesh;
      } else {
        skybox = result.meshes.find((m) => m.getTotalVertices() > 0) as Mesh;
      }

      // Ensure we found a mesh
      if (!skybox && result.meshes.length > 0) {
        skybox = result.meshes[0] as Mesh;
      }

      // Detach the skybox mesh from its parent hierarchy
      // This is important because GLB files often have nested TransformNodes with transformations that would interfere with skybox rendering
      if (skybox.parent) {
        skybox.setParent(null);
      }

      // Dispose all other meshes and the root node
      result.meshes.forEach((m) => {
        if (m !== skybox && !m.isDisposed()) {
          m.dispose();
        }
      });

      skybox.name = name + '_mesh';
      skybox.infiniteDistance = true;
      skybox.isPickable = false;
      skybox.checkCollisions = false;
      skybox.alwaysSelectAsActiveMesh = true;
      skybox.applyFog = false;
      skybox.doNotSyncBoundingInfo = true;

      // Disable backFaceCulling so we see the inside of the skybox
      if (skybox.material) {
        skybox.material.backFaceCulling = false;
      }

      // Apply scale
      const scale = config.scale || 10000;
      skybox.scaling.set(scale, scale, scale);

      if (config.position) {
        skybox.position.addInPlace(config.position);
      }

      if (config.rotation) {
        skybox.rotation = config.rotation.clone();
      }

      skybox.rotationQuaternion = null;

      if (config.rotationAxis) {
        scene.registerBeforeRender(() => {
          if (skybox && !skybox.isDisposed()) {
            skybox.rotation[config.rotationAxis] += rotationSpeed * scene.getEngine().getDeltaTime();
          }
        });
      }
      return skybox;
    }

    const skybox = MeshBuilder.CreateBox(
      name + '_mesh',
      {
        size: 10000.0,
      },
      scene
    );
    const skyboxMaterial = new StandardMaterial(name + '_material', scene);
    skyboxMaterial.backFaceCulling = false;
    skyboxMaterial.disableLighting = true;
    skyboxMaterial.fogEnabled = false;
    skybox.infiniteDistance = true;

    skyboxMaterial.diffuseColor = new Color3(0, 0, 0);
    skyboxMaterial.specularColor = new Color3(0, 0, 0);

    if (isHdr) {
      skyboxMaterial.reflectionTexture = new HDRCubeTexture(url, scene, 1024, false, false, false, true);
    }

    if (isSingle) {
      const files = await ImageUtils.generateCubeTextureImages(url);
      skyboxMaterial.reflectionTexture = CubeTexture.CreateFromImages(files, scene, true);
    } else if (!isHdr) {
      skyboxMaterial.reflectionTexture = new CubeTexture(url, scene, [
        '_px.' + config.imageExtension,
        '_py.' + config.imageExtension,
        '_pz.' + config.imageExtension,
        '_nx.' + config.imageExtension,
        '_ny.' + config.imageExtension,
        '_nz.' + config.imageExtension,
      ]);
    }

    skyboxMaterial.reflectionTexture.coordinatesMode = Texture.SKYBOX_MODE;

    // Set the scene environment texture to the skybox texture for PBR support
    scene.environmentTexture = skyboxMaterial.reflectionTexture;

    skybox.material = skyboxMaterial;
    skybox.isPickable = false;
    skybox.checkCollisions = false;
    skybox.applyFog = false;

    if (config.rotationAxis) {
      scene.registerBeforeRender(() => {
        if (skybox && !skybox.isDisposed()) {
          skybox.rotation[config.rotationAxis] += rotationSpeed * scene.getEngine().getDeltaTime();
        }
      });
    }
    return skybox;
  }

  static perlinDeform(mesh: Mesh, noise: Noise) {
    const positions = mesh.getVerticesData(VertexBuffer.PositionKind);
    const normals = mesh.getVerticesData(VertexBuffer.NormalKind);

    if (positions && normals) {
      for (let i = 0; i < positions.length; i += 3) {
        // position as 3D vektor
        const vertex = new Vector3(positions[i], positions[i + 1], positions[i + 2]);
        const normal = new Vector3(normals[i], normals[i + 1], normals[i + 2]);

        const noiseValue = noise.perlin2(vertex.x, vertex.y);
        // move vertex along the normals
        vertex.addInPlace(normal.scale(noiseValue));

        // push back to array
        positions[i] = vertex.x;
        positions[i + 1] = vertex.y;
        positions[i + 2] = vertex.z;
      }

      // apply updated positions
      mesh.updateVerticesData(VertexBuffer.PositionKind, positions);

      // recompute normals
      VertexData.ComputeNormals(positions, mesh.getIndices(), normals);
      mesh.updateVerticesData(VertexBuffer.NormalKind, normals);
    }
  }

  /**
   * Create thin instances for a mesh from transformation matrices
   */
  static createThinInstances(baseMesh: Mesh, transforms: Matrix[]): void {
    const matricesData = new Float32Array(16 * transforms.length);

    for (let i = 0; i < transforms.length; i++) {
      transforms[i].copyToArray(matricesData, i * 16);
    }

    baseMesh.thinInstanceSetBuffer('matrix', matricesData, 16);
    baseMesh.thinInstanceCount = transforms.length;
  }

  /**
   * Helper to check if a mesh is manifold (each edge shared by exactly 2 faces)
   */
  static isManifold(mesh: Mesh): boolean {
    const indices = mesh.getIndices();
    if (!indices) return false;

    // Build edge map: key "min-max" -> count
    const edgeCounts = new Map<string, number>();

    for (let i = 0; i < indices.length; i += 3) {
      const p1 = indices[i];
      const p2 = indices[i + 1];
      const p3 = indices[i + 2];

      const addEdge = (a: number, b: number) => {
        const key = a < b ? `${a}-${b}` : `${b}-${a}`;
        edgeCounts.set(key, (edgeCounts.get(key) || 0) + 1);
      };

      addEdge(p1, p2);
      addEdge(p2, p3);
      addEdge(p3, p1);
    }

    let isManifold = true;
    for (const [key, count] of edgeCounts) {
      if (count !== 2) {
        console.warn(`Edge ${key} is shared by ${count} faces (expected 2)`);
        isManifold = false;
        // break; // Keep going to see all errors
      }
    }
    return isManifold;
  }
}
